import React, { useState } from "react";
import ChatHeader from "./ChatHeader";
import MessageList from "./MessageList";
import ChatInput from "./ChatInput";
import ChatCollapsedButton from "./ChatCollapsedButton";
import {
  Message,
  ThemeConfig,
  getThemeValue,
  ConversationItem,
} from "../hooks/useVoiceWidget";
import { getStrings, Language } from "../i18n/strings";
import { NEUTRAL_PALETTE } from "../utils/neutralPalette";
import { HeyAiMicrophoneIcon } from "./HeyAiIcons";

interface ChatWindowProps {
  messages: Message[];
  transcript?: Message[];
  conversationItems?: ConversationItem[];
  onSendMessage: (text: string) => void | Promise<void>;
  onUserActivity?: () => void;
  mode?: "chat" | "voice" | "combined";
  isConnected?: boolean;
  isRecording?: boolean;
  isAgentSpeaking?: boolean;
  buttonState?: "idle" | "connecting" | "recording" | "disabled";
  onVoiceButtonClick?: () => void;
  onStopConversation?: () => void;
  isEmbedded?: boolean;
  theme?: Partial<ThemeConfig>;
  uiVariant?: "legacy" | "split-switch";
  /** When false, agent link cards render without a clickable button. */
  linkButtonsEnabled?: boolean;
  widgetName?: string | null;
  welcomeMessage?: string | null;
  avatarUrl?: string;
  language?: Language;
  /** Whether the agent is currently processing (no response yet after user sent a message) */
  isAgentThinking?: boolean;
}

const ChatWindow: React.FC<ChatWindowProps> = ({
  messages,
  transcript: _transcript = [],
  conversationItems = [],
  onSendMessage,
  onUserActivity,
  mode = "chat",
  isConnected = false,
  isRecording = false,
  isAgentSpeaking = false,
  buttonState = "idle",
  onVoiceButtonClick,
  onStopConversation,
  isEmbedded = false,
  theme,
  uiVariant = "legacy",
  linkButtonsEnabled = true,
  widgetName,
  welcomeMessage,
  avatarUrl,
  language,
  isAgentThinking = false,
}) => {
  const t = getStrings(language);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  // Split-switch text panel is a compact 360px by default; let the user grow it.
  const [isEnlarged, setIsEnlarged] = useState(false);
  const useNeutralTextWindow = uiVariant === "split-switch";
  const canResize = useNeutralTextWindow && !isEmbedded;
  const windowWidth = isEmbedded
    ? "100%"
    : useNeutralTextWindow
      ? "min(520px, calc(100vw - 32px))"
      : "100%";
  const windowHeight = isEmbedded
    ? "100%"
    : useNeutralTextWindow
      ? isEnlarged
        ? "min(640px, calc(100vh - 96px))"
        : "360px"
      : "500px";

  // Collapsed view - compact chat button
  if (!isExpanded && !isEmbedded) {
    return (
      <ChatCollapsedButton
        buttonState={buttonState}
        theme={theme}
        onExpand={() => {
          setIsClosing(false);
          setIsExpanded(true);
        }}
        onVoiceStart={mode === "combined" ? onVoiceButtonClick : undefined}
        avatarUrl={avatarUrl}
        language={language}
        mode={mode}
        uiVariant={uiVariant}
        widgetName={widgetName}
      />
    );
  }

  // Expanded view - full chat window
  const handleClose = () => {
    if (isEmbedded) {
      return;
    }

    if (!useNeutralTextWindow) {
      setIsExpanded(false);
      return;
    }

    setIsClosing(true);
    window.setTimeout(() => {
      setIsExpanded(false);
      setIsClosing(false);
    }, 170);
  };

  return (
    <div className="heyai-panel-stage" style={{ position: "relative" }}>
      <div
        data-testid={useNeutralTextWindow ? "heyai-text-panel" : undefined}
        className={
          useNeutralTextWindow
            ? `heyai-text-panel-motion${isClosing ? " heyai-text-panel-motion--closing" : ""}`
            : undefined
        }
        data-motion-state={isClosing ? "closing" : "open"}
        style={{
          width: windowWidth,
          height: windowHeight,
          maxWidth: useNeutralTextWindow ? "520px" : "350px",
          display: "flex",
          flexDirection: "column",
          background: useNeutralTextWindow
            ? "linear-gradient(145deg, rgba(255,255,255,0.58), rgba(255,255,255,0.34))"
            : getThemeValue(theme, "chatBackground"),
          backdropFilter: useNeutralTextWindow ? "blur(24px) saturate(145%)" : undefined,
          WebkitBackdropFilter: useNeutralTextWindow ? "blur(24px) saturate(145%)" : undefined,
          borderRadius: useNeutralTextWindow ? "20px" : getThemeValue(theme, "borderRadius"),
          border: useNeutralTextWindow
            ? "1px solid rgba(255, 255, 255, 0.52)"
            : undefined,
          boxShadow: useNeutralTextWindow
            ? "0 24px 70px rgba(10, 18, 32, 0.22), inset 0 1px 0 rgba(255,255,255,0.55)"
            : "0 4px 20px rgba(0, 0, 0, 0.1)",
          overflow: "hidden",
          fontFamily: getThemeValue(theme, "fontFamily"),
        }}
      >
        <ChatHeader
          widgetName={widgetName}
          avatarUrl={avatarUrl}
          isRecording={isRecording}
          isAgentSpeaking={isAgentSpeaking}
          isConnected={isConnected}
          mode={mode}
          uiVariant={uiVariant}
          showStopButton={isConnected || messages.length > 0}
          buttonState={buttonState}
          language={language}
          onVoiceButtonClick={onVoiceButtonClick}
          onStopConversation={onStopConversation}
          onClose={isEmbedded ? undefined : handleClose}
          isEnlarged={isEnlarged}
          onToggleEnlarge={canResize ? () => setIsEnlarged((v) => !v) : undefined}
        />

        <MessageList
          conversationItems={conversationItems}
          isAgentSpeaking={isAgentSpeaking}
          isAgentThinking={isAgentThinking}
          avatarUrl={avatarUrl}
          widgetName={widgetName}
          welcomeMessage={welcomeMessage}
          theme={theme}
          uiVariant={uiVariant}
          linkButtonsEnabled={linkButtonsEnabled}
          language={language}
        />

        <ChatInput
          onSendMessage={onSendMessage}
          onUserActivity={onUserActivity}
          theme={theme}
          uiVariant={uiVariant}
          language={language}
        />
      </div>

      {useNeutralTextWindow && onVoiceButtonClick && (
        <button
          data-testid="heyai-panel-voice-mic"
          className={`heyai-panel-voice-mic${isClosing ? " heyai-panel-voice-mic--closing" : ""}`}
          type="button"
          onClick={onVoiceButtonClick}
          disabled={buttonState === "disabled" || buttonState === "connecting"}
          aria-label={buttonState === "connecting" ? t.statusConnecting : t.startVoice}
          style={{
            position: "absolute",
            right: "18px",
            bottom: "18px",
            width: "56px",
            height: "56px",
            borderRadius: "50%",
            border: "1px solid rgba(255,255,255,0.76)",
            background:
              "radial-gradient(circle at 70% 85%, rgba(255,199,149,0.32), transparent 36%), radial-gradient(circle at 20% 15%, rgba(119,175,255,0.28), transparent 38%), rgba(255,255,255,0.58)",
            boxShadow:
              "0 16px 38px rgba(10, 18, 32, 0.22), inset 0 1px 0 rgba(255,255,255,0.74)",
            backdropFilter: "blur(20px) saturate(145%)",
            WebkitBackdropFilter: "blur(20px) saturate(145%)",
            display: "grid",
            placeItems: "center",
            padding: 0,
            color: NEUTRAL_PALETTE.anthracite,
            cursor:
              buttonState === "disabled" || buttonState === "connecting" ? "not-allowed" : "pointer",
            opacity: buttonState === "disabled" || buttonState === "connecting" ? 0.55 : 1,
          }}
        >
          <HeyAiMicrophoneIcon size={24} />
        </button>
      )}
    </div>
  );
};

export default ChatWindow;
